import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { AppConfig } from "../../../app.config";
import { map } from "rxjs/operators";
import { Observable, BehaviorSubject } from "rxjs";
import { Employee } from "../models/Employee/employee.model";
import { EmployeeFind } from "../models/Employee/employee-find.model.";
import { IPagedModel } from '../models/paged.model.';
import { CertificatePost } from "../models/certificatePost.model";
import { EmployeeFindApprover } from "../models/Employee/employeeFindApprover.model";
import {
  EmployeeBulkDeactivationEligibility,
  EmployeeBulkDeactivationRequest,
  EmployeeBulkDeactivationResponse
} from "../models/Employee/employee-bulk-deactivation.model";
import {
  EmployeeNotifyPendingDocumentationCreated,
  EmployeeNotifyPendingDocumentationEligibility,
  EmployeeNotifyPendingDocumentationRequest
} from "../models/Employee/employee-notify-pending-documentation.model";



@Injectable({ providedIn: 'root' })
export class EmployeeService {
  url = AppConfig.settings.apiUrls.cpp;
  employeeList = new BehaviorSubject<Employee[]>([]);

  constructor(private http: HttpClient) { }

  mapResponse(res: IPagedModel<Employee>): IPagedModel<Employee> {
    const employees: Employee[] = [];
    res.values.forEach(process => {
      employees.push(this.mapSingleResponse(process));
    });

    const response: IPagedModel<Employee> = {
      values: employees,
      itemPerPage: res.itemPerPage,
      page: res.page,
      total: res.total
    };

    this.employeeList.next(employees);
    return response;
  }

  mapSingleResponse(res: Employee): Employee {
    const response: Employee = new Employee();
    if (!res) {
      return response;
    }

    response.id = res.id;
    response.organizationalUnitId = res.organizationalUnitId;
    response.organizationalUnitName = res.organizationalUnitName;
    response.metadatas = res.metadatas;
    response.containerTypeId = res.containerTypeId;
    response.nickName = res.nickName;
    response.delegatedSystemId = res.delegatedSystemId;
    response.hasActiveCertificate = res.hasActiveCertificate;
    response.preview = res.preview;
    response.hasLeaveConfig = res.hasLeaveConfig;
    return response;
  }

  createEmployee(employee: Employee, sendWelcome: boolean = true): Observable<Employee> {
    let params = new HttpParams();
    params = params.append('sendWelcome', sendWelcome.toString());

    return this.http.post<Employee>(
      `${this.url}/Employees`,
      employee, { params: params }
    );
  }

  modifyEmployee(employee: Employee): Observable<Employee> {
    return this.http.put<Employee>(
      `${this.url}/Employees`,
      employee
    );
  }

  updatePhone(phone: string): Observable<Employee> {
    // const phoneNumber = { phone }
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type':'application/json' })
    }
    return this.http.put<Employee>(
      `${this.url}/Containers/UpdatePhone`,'"' + phone +'"',httpOptions);
  }

  deleteEmployee(id: number, disableUser: boolean): Observable<Employee> {
    return this.http.delete<Employee>(
      `${this.url}/Employees/${id}?disableUser=${disableUser}`
    );
  }

  checkBulkDeactivationEligibility(param: EmployeeBulkDeactivationRequest): Observable<EmployeeBulkDeactivationEligibility> {
    return this.http.post<EmployeeBulkDeactivationEligibility>(
      `${this.url}/Employees/BulkDeactivation/Eligibility`,
      param
    );
  }

  bulkDeactivation(param: EmployeeBulkDeactivationRequest): Observable<EmployeeBulkDeactivationResponse> {
    return this.http.post<EmployeeBulkDeactivationResponse>(
      `${this.url}/Employees/BulkDeactivation`,
      param
    );
  }

  // BA-016 (ECM-15522): notificación de documentación pendiente a los empleados de una OU por POS
  checkNotifyPendingDocumentationEligibility(param: EmployeeNotifyPendingDocumentationRequest): Observable<EmployeeNotifyPendingDocumentationEligibility> {
    return this.http.post<EmployeeNotifyPendingDocumentationEligibility>(
      `${this.url}/Employees/NotifyPendingDocumentation/Eligibility`,
      param
    );
  }

  notifyPendingDocumentation(param: EmployeeNotifyPendingDocumentationRequest): Observable<HttpResponse<EmployeeNotifyPendingDocumentationCreated>> {
    return this.http.post<EmployeeNotifyPendingDocumentationCreated>(
      `${this.url}/Employees/NotifyPendingDocumentation`,
      param,
      { observe: "response" }
    );
  }

  hardDeleteEmployee(employeeid: number, userId: number,isCandidate : boolean) : Observable<boolean> {
    return this.http.delete<boolean>(
      `${this.url}/Employees/HardDelete/${employeeid}/${userId}/${isCandidate}`
    );
  }

  activeEmployee(emp: Employee): Observable<Employee> {
    const param: EmployeeFind = {
      containerTypeId: emp.containerTypeId,
      organizationUnitIds: [emp.organizationalUnitId],
      userid: emp.userId,
      cuil: emp.cuil,
      active: true
    };

    return this.http.put<Employee>(
      `${this.url}/Employees/Active/${emp.id}`, param
    );
  }

  getContainers(model: EmployeeFind): Observable<IPagedModel<Employee>> {
    return this.http
      .put<IPagedModel<Employee>>(
        `${AppConfig.settings.apiUrls.cpp}/Employees/Find`,
        model
      )
      .pipe(map(res => this.mapResponse(res)));
  }

  getContainer(id: string): Observable<Employee> {
    return this.http
      .get<Employee>(
        `${AppConfig.settings.apiUrls.cpp}/Employees/${id}`
      )
      .pipe(map(res => this.mapSingleResponse(res)));
  }

  getTeamContainer(model: EmployeeFind): Observable<IPagedModel<Employee>> {
    return this.http
      .put<IPagedModel<Employee>>(
        `${AppConfig.settings.apiUrls.cpp}/leaves/GetTeamEmployees`,
        model
      )
      .pipe(map(res => this.mapResponse(res)));
  }

  getTeamEmployee(model: EmployeeFindApprover): Observable<Employee> {
    return this.http
      .put<Employee>(
        `${AppConfig.settings.apiUrls.cpp}/leaves/GetTeamEmployeeById`,
        model
      )
      .pipe(map(res => this.mapSingleResponse(res)));
  }

  certificateDeclaration(certificate: CertificatePost, files: File[]) {
    const formData: FormData = new FormData();
    if (files && files.length > 0) {

      for (let index = 0; index < files.length; index++) {
        const file = files[index];
        formData.append(file.name, file, file.name);
      }
    }
    formData.append('dto', JSON.stringify(certificate));
    return this.http.post(`${this.url}/Employees/CertDeclaration`, formData);
  }

  getShareEmployeeNotif(id: string): Observable<Employee> {
    return this.http
      .get<Employee>(
        `${AppConfig.settings.apiUrls.cpp}/Employees/ShareEmployeeNotif/${id}`
      )
      .pipe(map(res => this.mapSingleResponse(res)));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sharePerson(dto: any) {
    return this.http
      .post<Employee>(
        `${AppConfig.settings.apiUrls.cpp}/Employees/ShareEmployee`,
        dto
      )
      .pipe(map(res => this.mapSingleResponse(res)));
  }
}
